import { defaultLocale, type Locale } from "@/config/locale";

/**
 * Public storefront path for a locale: German (`de`) has no `/de` prefix;
 * `en`, `es`, `tr` use `/{locale}/...`.
 */
export function publicStorePath(locale: string, path: string): string {
	const normalized = !path || path === "/" ? "/" : path.startsWith("/") ? path : `/${path}`;

	if (locale === defaultLocale) {
		return normalized === "/" ? "/" : normalized;
	}

	const loc = locale as Locale;
	if (normalized === "/") {
		return `/${loc}`;
	}
	return `/${loc}${normalized}`;
}

/**
 * Absolute public URL. The German homepage is the origin without a trailing
 * slash, matching the HTML canonical. Other paths keep their slash.
 */
export function absolutePublicUrl(origin: string, locale: string, path: string): string {
	const root = origin.replace(/\/$/, "");
	const publicPath = publicStorePath(locale, path);
	return publicPath === "/" ? root : `${root}${publicPath}`;
}

function normalizePathname(pathname: string): string {
	if (!pathname || pathname === "") return "/";
	return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

/**
 * Maps the visible URL pathname to the canonical “public” path (German-style, no `/de` prefix)
 * for the given active locale. German may be shown as `/…` or legacy `/de/…`; non-default
 * locales use `/{locale}/…`.
 */
export function toPublicStorePath(pathname: string, currentLocale: Locale): string {
	const normalized = normalizePathname(pathname);

	if (currentLocale === defaultLocale) {
		if (normalized === "/de" || normalized === "/de/") {
			return "/";
		}
		if (normalized.startsWith("/de/")) {
			const rest = normalized.slice("/de".length);
			return rest === "" ? "/" : rest;
		}
		return normalized;
	}

	const prefix = `/${currentLocale}`;
	if (normalized === prefix || normalized === `${prefix}/`) {
		return "/";
	}
	if (normalized.startsWith(`${prefix}/`)) {
		const rest = normalized.slice(prefix.length);
		return rest === "" ? "/" : rest;
	}

	return normalized;
}

/** Build the visible storefront URL path when switching locale while keeping the same page. */
export function switchStorefrontLocalePath(pathname: string, fromLocale: Locale, toLocale: Locale): string {
	const publicPath = toPublicStorePath(pathname, fromLocale);
	return publicStorePath(toLocale, publicPath);
}
